import {
  Investor,
  KycProfileStatusOption,
  TaxResidency,
} from "@ifgengineering/client-invest-sdk";
import { RootState } from "../rootReducer";
import { differenceInDays } from "date-fns";

export const verifyInvestorStatusSelector = (state: RootState): boolean => {
  const investor = state.investor?.data;
  const isOnboardingCompleted = investor?.onboardingStatus === "COMPLETED";

  const profileType = investor?.accreditationProfile?.type;
  const isHighNetWorthInvestor = profileType === "HIGH_NET_WORTH";
  const isSelfCertifiedInvestor = profileType === "SELF_CERTIFIED";

  const investorSignedDate = investor?.accreditationProfile?.signedAt;

  const shouldRecertify = investorSignedDate
    ? differenceInDays(new Date(), new Date(investorSignedDate)) > 365
    : false;

  return (
    (isHighNetWorthInvestor || isSelfCertifiedInvestor) &&
    isOnboardingCompleted &&
    shouldRecertify
  );
};

export const isOnboardingCompleteSelector = (state: RootState): boolean =>
  state?.investor?.data?.onboardingStatus === "COMPLETED";

/**
 * Derives the current onboarding step (0..4) from the investor record, mirroring
 * mobile's `useOnboardingHelper`
 * (`cur8-mob/src/screens/homepage/helpers/onboarding.helper.tsx`) so web and
 * mobile resolve the same step from the same fields. If onboarding is already
 * marked COMPLETED we trust that over field-level checks (data may change later).
 */
export const deriveOnboardingStep = (
  investor: Partial<Investor> | null
): number => {
  const hasEligibilityInfo = !!investor?.accreditationProfile?.signedAt;
  const hasPersonalInfo =
    !!investor?.firstName &&
    !!investor?.lastName &&
    !!investor?.gender &&
    !!investor?.dob &&
    !!investor?.mobileNumber &&
    !!investor?.profession;
  const hasAddressInfo =
    !!investor?.address?.city &&
    !!investor?.address?.country &&
    !!investor?.address?.addressLine1 &&
    !!investor?.address?.postcode;
  const hasLegalInfo =
    !!(investor?.funds || []).filter(Boolean).length &&
    investor?.isUkTaxResident !== undefined &&
    !!investor?.nationality;
  const hasBasicInfo = hasPersonalInfo && hasAddressInfo && hasLegalInfo;
  const hasApprovedKyc = investor?.kycProfile?.status === "APPROVED";
  const hasCompletedQuiz = investor?.onboardingStatus === "COMPLETED";

  if (hasCompletedQuiz && hasApprovedKyc) return 4;
  if (hasCompletedQuiz) return 3;
  if (hasEligibilityInfo && hasBasicInfo) return 2;
  if (hasEligibilityInfo) return 1;
  return 0;
};

export const onboardingStepSelector = (state: RootState): number =>
  deriveOnboardingStep(state.investor?.data || null);

export const investorSelector = (state: RootState): Partial<Investor> | null =>
  state.investor.data || null;

export const isLoadingInvestorSelector = (state: RootState): boolean =>
  state.investor.loading;

export const neverLoadedInvestorSelector = (state: RootState): boolean =>
  state.investor.neverLoaded;

export const investorBankDetailsSelector = (
  state: RootState
): Investor["bankDetails"] => state.investor.data?.bankDetails;

export const hasKYCStartedSelector = (state: RootState): boolean =>
  !!state.investor.data?.kycProfile?.status &&
  state.investor.data.kycProfile.status !== "NOT_STARTED";

/**
 * Statuses the investor themselves has to act on. The rest are with us, so the
 * verification flow has nothing to offer and sends them back out.
 */
const KYC_AWAITING_INVESTOR: KycProfileStatusOption[] = [
  "NOT_STARTED",
  "PENDING_USER_DOCUMENTS",
  "REJECTED",
  "EXPIRED",
];

export const isKYCAwaitingInvestor = (
  status?: KycProfileStatusOption
): boolean => !status || KYC_AWAITING_INVESTOR.includes(status);

export const isKYCAwaitingInvestorSelector = (state: RootState): boolean =>
  isKYCAwaitingInvestor(state.investor.data?.kycProfile?.status);

export const hasKYCFinishedSelector = (state: RootState): boolean =>
  !!state.investor.data?.kycProfile?.status &&
  state.investor.data.kycProfile.status === "APPROVED";

export const hasCompletedMandatoryQuestionsSelector = (
  state: RootState
): boolean => !!state.investor.data?.mandatoryQuestionnaireResult?.completedAt;

export const shouldShowTaxBannerSelector = (state: RootState): boolean =>
  state.investor.data?.shouldShowTaxBanner || false;

export const taxResidenciesSelector = (state: RootState): TaxResidency[] =>
  state.investor.data?.taxResidencies || [];

export const getTaxResidencyByIdSelector = (
  state: RootState,
  id: string
): TaxResidency | undefined =>
  state.investor.data?.taxResidencies?.find((residency) => residency.id === id);

export const investorNameSelector = (state: RootState): string =>
  `${state.investor.data?.firstName} ${state.investor.data?.lastName}`;

export const getInvestorHasUkTaxResidencySelector = (
  state: RootState
): boolean =>
  state.investor.data?.taxResidencies?.some(
    (residency) => residency.jurisdiction === "GB"
  ) || false;

/**
 * An IF-ISA needs the National Insurance number as well as the residency: the
 * API refuses to open an account without one, so a residency alone is not
 * access.
 */
export const getInvestorHasUkTaxResidencyAndTINSelector = (
  state: RootState
): boolean =>
  state.investor.data?.taxResidencies?.some(
    (residency) =>
      residency.jurisdiction === "GB" && !residency.deletedAt && !!residency.tin
  ) || false;

export const getInvestorHasUsTaxResidencySelector = (
  state: RootState
): boolean =>
  state.investor.data?.taxResidencies?.some(
    (residency) => residency.jurisdiction === "US"
  ) || false;
